import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface Motivo { id: number; nome: string; ativo: boolean }

export default function MotivosDevolucao() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Motivo | null>(null)

  const { data: motivos = [], isLoading } = useQuery<Motivo[]>({
    queryKey: ['motivos-devolucao'],
    queryFn: async () => (await api.get('/cadastros/motivos-devolucao')).data,
  })

  const salvar = useMutation({
    mutationFn: async (m: Motivo) => {
      const body = { nome: m.nome, ativo: m.ativo }
      return m.id
        ? (await api.put(`/cadastros/motivos-devolucao/${m.id}`, body)).data
        : (await api.post('/cadastros/motivos-devolucao', body)).data
    },
    onSuccess: () => {
      toast.success('Motivo salvo')
      setModal(null)
      qc.invalidateQueries({ queryKey: ['motivos-devolucao'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/cadastros/motivos-devolucao/${id}`)).data,
    onSuccess: () => {
      toast.success('Motivo excluído')
      qc.invalidateQueries({ queryKey: ['motivos-devolucao'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Motivos de Devolução</h2>
          <p className="text-sm text-ink-500">Usados (obrigatório) no acerto de carga quando há devolução.</p>
        </div>
        <button onClick={() => setModal({ id: 0, nome: '', ativo: true })} className="btn-primary">
          <Plus size={14} /> Novo motivo
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Motivo</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={3} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && motivos.length === 0 && (
              <tr><td colSpan={3} className="text-center py-8 text-ink-500">Nenhum motivo cadastrado.</td></tr>
            )}
            {motivos.map((m) => (
              <tr key={m.id} className="hover:bg-violet-50">
                <td className="font-medium">{m.nome}</td>
                <td>{m.ativo ? <span className="badge-success">Ativo</span> : <span className="badge-info">Inativo</span>}</td>
                <td className="text-right">
                  <button onClick={() => setModal(m)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => pedirExclusao({ descricao: `o motivo "${m.nome}"`, onConfirm: () => excluir.mutate(m.id) })} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Excluir"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold">{modal.id ? 'Editar motivo' : 'Novo motivo'}</h3>
              <button onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label">Motivo</label>
                <input className="input" autoFocus value={modal.nome}
                  onChange={(e) => setModal({ ...modal, nome: e.target.value })}
                  placeholder="Ex: Cliente recusou, Produto avariado, Endereço não encontrado..." />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={modal.ativo} onChange={(e) => setModal({ ...modal, ativo: e.target.checked })} />
                Ativo (aparece na lista do acerto)
              </label>
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setModal(null)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => salvar.mutate(modal)} disabled={!modal.nome.trim() || salvar.isPending} className="btn-primary flex-1">
                {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
